// Phase N23-B — Entity Panel (Province ↔ District ↔ BTS/MRT ↔ Mall)
// Reads getDistrictContext(slug) and renders a compact, speakable
// knowledge panel for AI Overview / SGE / Perplexity to traverse the
// hyper-local entity graph. No Review/AggregateRating — pure facts.
import { getDistrictContext } from "@/data/district-context";

interface EntityPanelProps {
  slug: string;
  locationName: string; // display name (TH or EN)
  locale?: "th" | "en";
  variant?: "dark" | "light";
}

export function EntityPanel({ slug, locationName, locale = "th", variant = "dark" }: EntityPanelProps) {
  const ctx = getDistrictContext(slug);
  const total = ctx.landmarks.length + ctx.transit.length + ctx.malls.length + ctx.neighbors.length;
  if (total === 0) return null;

  const L = locale === "en"
    ? { title: `About ${locationName} — local entity graph`, transit: "Transit (BTS/MRT)", landmarks: "Landmarks", malls: "Malls", neighbors: "Neighboring districts", note: "Verifiable, publicly-known places used for travel planning." }
    : { title: `เกี่ยวกับ ${locationName} — ข้อมูลพื้นที่`, transit: "สถานี BTS/MRT ใกล้เคียง", landmarks: "สถานที่สำคัญ", malls: "ห้างสรรพสินค้า", neighbors: "เขต/พื้นที่ติดกัน", note: "ข้อมูลสาธารณะใช้สำหรับการเดินทาง" };

  const dark = variant === "dark";
  const wrap = dark
    ? "rounded-2xl border border-white/10 bg-white/[0.04] p-5 sm:p-6"
    : "rounded-2xl border border-slate-200 bg-slate-50 p-5 sm:p-6";
  const titleCls = dark ? "text-base font-bold text-amber-300 sm:text-lg" : "text-base font-bold text-navy-deep sm:text-lg";
  const labelCls = dark ? "text-[10px] font-bold uppercase tracking-widest text-white/60" : "text-[10px] font-bold uppercase tracking-widest text-slate-500";
  const chipCls = dark
    ? "inline-block rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 text-[11px] font-medium text-white/85"
    : "inline-block rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-slate-700";
  const noteCls = dark ? "mt-3 text-[11px] text-white/55" : "mt-3 text-[11px] text-slate-500";

  const Row = ({ label, items }: { label: string; items: string[] }) =>
    items.length === 0 ? null : (
      <div className="mt-3">
        <div className={labelCls}>{label}</div>
        <ul className="mt-1.5 flex flex-wrap gap-1.5" data-speakable="true">
          {items.map((it) => (
            <li key={it} className={chipCls} itemProp="containedInPlace" itemScope itemType="https://schema.org/Place">
              <span itemProp="name">{it}</span>
            </li>
          ))}
        </ul>
      </div>
    );

  return (
    <section className="px-4 py-8 sm:px-6">
      <div
        className={`mx-auto max-w-5xl ${wrap}`}
        itemScope
        itemType="https://schema.org/Place"
        aria-label={L.title}
      >
        <h2 className={titleCls} itemProp="name">{L.title}</h2>
        <Row label={L.transit} items={ctx.transit} />
        <Row label={L.landmarks} items={ctx.landmarks} />
        <Row label={L.malls} items={ctx.malls} />
        <Row label={L.neighbors} items={ctx.neighbors} />
        <p className={noteCls}>{L.note}</p>
      </div>
    </section>
  );
}
